import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import API_BASE_URL from "../../../config";
import AssessmentTopNav from "./topnav";
import Sidebar from "./Sidebar";
import axios from "axios";
import "../../../Styles/Admin/assessment/EmployeeReport.css";

import "../../../axios_overrite";

/* ======================================================
   Static PNG icon assets (client/public/*).
   Referenced by public-root path since that's how CRA/Vite
   serve files placed in /public — an absolute Windows path
   can't be resolved by the browser.
   ====================================================== */
const PUBLIC_URL = process.env.PUBLIC_URL || "";
// Bump this whenever you replace an icon file in client/public/ with new
// artwork but keep the same filename — browsers cache unfingerprinted
// /public assets aggressively, so without a version query string a
// replaced icon can keep showing the old cached image indefinitely.
const ICON_VERSION = "2";
const withVersion = (path) => `${path}?v=${ICON_VERSION}`;

const ICON_DATE = withVersion(`${PUBLIC_URL}/report_date_icons.png`);
const ICON_CORPORATE = withVersion(`${PUBLIC_URL}/report_office_employee_icons.png`);
const ICON_FILTER = withVersion(`${PUBLIC_URL}/report_tabler_icons.png`);
// Kept only for reference — the standalone search box it belonged to has
// been removed (search now lives inside the All Companies dropdown).
const ICON_SEARCH = withVersion(`${PUBLIC_URL}/report_search_icons.png`);
const ICON_UPLOAD = withVersion(`${PUBLIC_URL}/report_upload_icons.png`);
const ICON_OFFICE = withVersion(`${PUBLIC_URL}/report_corporate_icons.png`);
const ICON_EMPLOYEE_GROUP = withVersion(`${PUBLIC_URL}/report_employee_group_icons.png`);
const ICON_COMPLETED = withVersion(`${PUBLIC_URL}/report_fluent_completed_icons.png`);
const ICON_IN_PROGRESS = withVersion(`${PUBLIC_URL}/report_in_progress_icons.png`);
const ICON_ARROW_DOWN = withVersion(`${PUBLIC_URL}/report_arrow_down_icons.png`);
const ICON_EMPTY_STATE = withVersion(`${PUBLIC_URL}/report_bg_img.png`);
const ICON_ASSESSMENT = withVersion(`${PUBLIC_URL}/tabler_report_ass_icons.png`);
const ICON_EDIT = withVersion(`${PUBLIC_URL}/report_edit_icons.png`);
const ICON_DELETE = withVersion(`${PUBLIC_URL}/report_delete_icons.png`);
const ICON_DELETE_ALERT = withVersion(`${PUBLIC_URL}/report_delete_alert_icons.png`);
const ICON_ASSESSMENT_BACK = withVersion(`${PUBLIC_URL}/report_ass_arrow.png`);
const ICON_ASSESSMENT_VIEW = withVersion(`${PUBLIC_URL}/report_ass_icons_view.png`);
const ICON_ASSESSMENT_FILTER = withVersion(`${PUBLIC_URL}/report_ass_Vector.png`);
const ICON_ASSESSMENT_ANSWER = withVersion(`${PUBLIC_URL}/report_ass_ans_icon.png`);
// Navy card-header icons ("Participation & Response Rate" / "Response
// Summary ( Overall )") — drop your Figma exports in /public with these
// names, or change the paths. Rendered inside a 32x32 rounded box.
const ICON_HEAD_RESPONSE = withVersion(`${PUBLIC_URL}/report_head_response_icons.png`);
const ICON_HEAD_SUMMARY = withVersion(`${PUBLIC_URL}/report_head_summary_icons.png`);

const emrCssVars = {
  "--rep_emr-arrow-icon": `url(${ICON_ARROW_DOWN})`,
  "--rep_emr-ans-icon": `url(${ICON_ASSESSMENT_ANSWER})`,
  "--rep_emr-inprogress-icon": `url(${ICON_IN_PROGRESS})`,
};

const departmentOptions = [
  "Human Resources (HR)",
  "Finance & Accounts",
  "Sales & Marketing",
  "Information Technology (IT)",
  "Operations & Administration",
  "IT Support",
  "Customer Support / Customer Service",
  "Business Development",
  "Procurement / Purchase",
  "Legal & Compliance",
  "Training & Development",
  "Maintenance, Quality & Production",
  "Research & Development (R&D)",
  "Audit & Risk Management",
  "Facilities Management",
  "Medical Coding",
  "Field Services",
  "Warehouse & Logistics",
  "Service Delivery",
  "Others",
];

const RESPONSE_LEVELS = [
  { key: "strongly_agree", label: "Strongly Agree", color: "#007221" },
  { key: "agree", label: "Agree", color: "#00A631" },
  { key: "neutral", label: "Neutral", color: "#FECD4C" },
  { key: "disagree", label: "Disagree", color: "#F28500" },
  { key: "strongly_disagree", label: "Strongly Disagree", color: "#F24822" },
];

// Used when nobody has answered yet (admin AND customer view), so no fake
// sample numbers are ever shown.
const EMPTY_RESPONSE_COUNTS = {
  strongly_agree: 0,
  agree: 0,
  neutral: 0,
  disagree: 0,
  strongly_disagree: 0,
};

// Value used by the customer year dropdown for the combined view.
const ALL_YEARS = "all";

// Answer options for the Assessment Taken "View Details" question list.
const LIKERT_OPTIONS = [
  { key: "strongly_agree", label: "Strongly Agree" },
  { key: "agree", label: "Agree" },
  { key: "neutral", label: "Neutral" },
  { key: "disagree", label: "Disagree" },
  { key: "strongly_disagree", label: "Strongly Disagree" },
];

const CORRECT_COLOR = "#00A631";
const INCORRECT_COLOR = "#FF0000";

const isAnswerCorrect = (question) => {
  if (typeof question.is_correct === "boolean") return question.is_correct;
  return (
    question.selected_answer === "agree" ||
    question.selected_answer === "strongly_agree"
  );
};

// The stored answer text ("Agree", "Strongly Disagree", etc.) needs to be
// converted to the lowercase/underscore key format LIKERT_OPTIONS and the
// question-list rendering expect ("agree", "strongly_disagree", ...).
const mapAnswerToKey = (answerText) => {
  if (!answerText) return null;
  return answerText.trim().toLowerCase().replace(/\s+/g, "_");
};

// Section order for the quiz list — matches AssessmentQuestions.js so
// sections always display in the same order the employee took them in.
const ASSESSMENT_SECTION_ORDER = [
  "Trust & Leadership",
  "Employee Experience & Engagement",
  "Well-being & Inclusion",
  "Workplace Practices & Innovation",
  "Sustainability & Social Impact",
];

const getInitials = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

// Builds a page-number list with "..." gaps for large page counts, e.g.
// [1, "...", 4, 5, 6, "...", 12] — keeps the pagination bar from ever
// needing to render dozens of page buttons in a row.
const getPaginationRange = (current, total) => {
  const delta = 1;
  const range = [];
  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
      range.push(i);
    }
  }
  const withDots = [];
  let last;
  range.forEach((i) => {
    if (last !== undefined) {
      if (i - last === 2) {
        withDots.push(last + 1);
      } else if (i - last > 2) {
        withDots.push("...");
      }
    }
    withDots.push(i);
    last = i;
  });
  return withDots;
};

const getAssessmentProgress = (emp) => {
  const status = emp.assessment_status || "Not Started";
  const progress =
    typeof emp.progress === "number"
      ? emp.progress
      : status === "Completed"
      ? 100
      : 0;
  return { status, progress };
};

// Half-circle "Response Rate" gauge.
/* ------------------------------------------------------------------
   Scroll / enter animations (same system as the Analytics page)
   - useScrollReveal: every [data-reveal="id"] element is revealed (data-in="1")
     the moment it scrolls into view; also picks up elements that appear later.
   - useCountUp / CountUp: numbers that count up from 0 once revealed.
   Skipped entirely for people who prefer reduced motion.
------------------------------------------------------------------ */
const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  !!window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function useScrollReveal(resetKey) {
  const [seen, setSeen] = useState({});
  useEffect(() => {
    setSeen({});
    document.querySelectorAll("[data-reveal]").forEach((el) => {
      delete el.dataset.in;
    });
    const tracked = new WeakSet();
    const reduced = prefersReducedMotion() || typeof IntersectionObserver === "undefined";
    const reveal = (el) => {
      el.dataset.in = "1";
      const id = el.dataset.reveal;
      setSeen((prev) => (prev[id] ? prev : { ...prev, [id]: true }));
    };
    const io = reduced
      ? null
      : new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                reveal(entry.target);
                io.unobserve(entry.target);
              }
            });
          },
          { threshold: 0.18, rootMargin: "0px 0px -5% 0px" }
        );
    const scan = () => {
      document.querySelectorAll("[data-reveal]").forEach((el) => {
        if (tracked.has(el)) return;
        tracked.add(el);
        if (reduced) reveal(el);
        else io.observe(el);
      });
    };
    scan();
    let raf = 0;
    const mo = new MutationObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(scan);
    });
    mo.observe(document.body, { childList: true, subtree: true });
    return () => {
      mo.disconnect();
      if (io) io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [resetKey]);
  return seen;
}

function useCountUp(target, run, duration = 1300, delay = 0) {
  const [val, setVal] = useState(0);
  const from = useRef(0);
  useEffect(() => {
    const t = Number(target);
    if (!run || !isFinite(t)) {
      from.current = 0;
      setVal(0);
      return undefined;
    }
    if (prefersReducedMotion()) {
      from.current = t;
      setVal(t);
      return undefined;
    }
    const a = from.current;
    let raf = 0;
    let start = null;
    const timer = setTimeout(() => {
      const step = (ts) => {
        if (start === null) start = ts;
        const p = Math.min(1, (ts - start) / duration);
        const eased = 1 - Math.pow(1 - p, 3);
        const v = a + (t - a) * eased;
        from.current = v;
        setVal(v);
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, delay);
    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(raf);
    };
  }, [target, run]);
  return Math.round(val);
}

function CountUp({ value, run, delay = 150, duration = 1200 }) {
  const n = typeof value === "number" ? value : Number(value);
  const numeric = typeof value === "number" || (typeof value === "string" && value.trim() !== "" && isFinite(n));
  const shown = useCountUp(numeric ? n : 0, run, duration, delay);
  return numeric ? shown.toLocaleString() : value;
}

const ResponseGauge = ({ percent = 0, animate = true }) => {
  const r = 78;
  const circumference = Math.PI * r;
  const filled = Math.max(0, Math.min(100, percent));
  const filledLength = (filled / 100) * circumference;
  const shownPercent = useCountUp(filled, animate, 1500, 350);

  return (
    <div className="rep_emr-gauge">
      <svg viewBox="0 0 200 116" className="rep_emr-gauge-svg">
        <path
          d={`M 22 100 A ${r} ${r} 0 0 1 178 100`}
          fill="none"
          stroke="#E5E7EB"
          strokeWidth="16"
          strokeLinecap="round"
        />
        <path
          d={`M 22 100 A ${r} ${r} 0 0 1 178 100`}
          fill="none"
          stroke="#00A631"
          strokeWidth="16"
          strokeLinecap="round"
          className="rep_emr-gauge-fill"
          strokeDasharray={`${animate ? filledLength : 0} ${circumference}`}
        />
      </svg>
      <div className="rep_emr-gauge-center">
        <span className="rep_emr-gauge-percent">{shownPercent}%</span>
      </div>
      <div className="rep_emr-gauge-scale">
        <span>0%</span>
        <span>100%</span>
      </div>
      <div className="rep_emr-gauge-caption">Response Rate</div>
    </div>
  );
};

/* ------------------------------------------------------------------
   Dropdown keyboard support (kept inside this file on purpose).

   Same contract for every dropdown on this page:
     Closed : Enter / Space / ArrowDown / ArrowUp opens the list.
              Typing a letter opens it too (company box: starts search).
     Open   : ArrowUp / ArrowDown move the highlight (wraps around)
              Home / End = first / last (not inside the search box)
              Enter picks the highlighted option
              Space picks it too (not in the company search box)
              Escape closes and returns focus to the box
              Tab closes and moves on to the next control
     Any    : ArrowLeft / ArrowRight jumps to the previous / next dropdown
              (Year <-> Company <-> Department); the next one opens if this one was open
   ------------------------------------------------------------------ */
let dropdownUidCounter = 0;
function useDropdownUid(prefix) {
  const ref = useRef(null);
  if (ref.current === null) {
    dropdownUidCounter += 1;
    ref.current = `${prefix}-${dropdownUidCounter}`;
  }
  return ref.current;
}

function normalizeSearchText(str) {
  return String(str || "").toLowerCase().replace(/[^a-z0-9]/gi, "");
}

function isPrintableKey(e) {
  return e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
}

// After a selection the box goes back to its normal (un-highlighted) look
// but keeps keyboard focus, so Left / Right / Tab still work from it. The
// focus ring comes back on the next key press or when focus leaves.
// (CSS: [data-quiet="1"] on the box's wrapper hides the ring.)
function setQuietFocus(triggerEl, quiet) {
  const wrap = triggerEl && triggerEl.parentElement;
  if (!wrap) return;
  if (quiet) wrap.setAttribute("data-quiet", "1");
  else wrap.removeAttribute("data-quiet");
}

// Left / Right arrow = jump to the previous / next dropdown on the page
// (Year <-> Company <-> Department). If the dropdown you are leaving was
// open, the next one opens too, like a menu bar. Inside the company search
// box the arrows keep moving the text caret and only jump once the caret
// is already at the left / right edge of the text.
function handleDropdownSideKey(e, triggerEl, isOpen) {
  setQuietFocus(triggerEl, false);
  if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return false;
  if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return false;
  const dir = e.key === "ArrowRight" ? 1 : -1;
  const el = e.target;
  if (el && el.tagName === "INPUT") {
    if (el.selectionStart !== el.selectionEnd) return false;
    if (dir === 1 && el.selectionStart !== el.value.length) return false;
    if (dir === -1 && el.selectionStart !== 0) return false;
  }
  const all = Array.from(document.querySelectorAll("[data-dd-nav]")).filter(
    (node) => !node.disabled && node.getAttribute("aria-disabled") !== "true" && node.offsetParent !== null
  );
  const idx = all.indexOf(triggerEl);
  const next = idx >= 0 ? all[idx + dir] : null;
  if (!next) return false;
  e.preventDefault();
  next.focus();
  if (isOpen) next.click();
  return true;
}

// Filters [{value,label}] by typed text. The blank-value "All ..." entry
// always stays at the top.
function filterCompanyOptions(options, text) {
  const term = normalizeSearchText(text);
  return options.filter((o) => o.value === "" || normalizeSearchText(o.label).includes(term));
}

// Which row should be highlighted: the selected one when the search is
// empty, otherwise the first real match (never "All ..."), or -1 if none.
function getCompanyActiveIndex(options, text, value) {
  const list = filterCompanyOptions(options, text);
  if (!normalizeSearchText(text)) {
    const idx = list.findIndex((o) => o.value === value);
    return idx >= 0 ? idx : 0;
  }
  return list.findIndex((o) => o.value !== "");
}

function useDropdownNav({ open, labels, onPick, typeAhead = false, spaceSelects = false, homeEnd = true }) {
  const [activeIndex, setActiveIndex] = useState(-1);
  const listRef = useRef(null);
  const scrollRef = useRef(true);
  const typeState = useRef({ text: "", timer: null }).current;

  // scroll=false is used for mouse hover so the list never jumps under the pointer
  const activate = (index, scroll = true) => {
    scrollRef.current = scroll;
    setActiveIndex(index);
  };

  useEffect(() => {
    if (!open || activeIndex < 0 || !scrollRef.current || !listRef.current) return;
    const list = listRef.current;
    const el = list.querySelector(`[data-nav-index="${activeIndex}"]`);
    if (!el) return;
    const top = el.offsetTop;
    const bottom = top + el.offsetHeight;
    if (top < list.scrollTop) list.scrollTop = Math.max(0, top - 6);
    else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight + 6;
  }, [open, activeIndex]);

  useEffect(() => () => clearTimeout(typeState.timer), [typeState]);

  // Returns true when the key was consumed.
  const handleNavKey = (e) => {
    const count = labels.length;
    if (e.ctrlKey || e.metaKey || e.altKey) return false;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (count) activate((activeIndex + 1) % count);
        return true;
      case "ArrowUp":
        e.preventDefault();
        if (count) activate(activeIndex <= 0 ? count - 1 : activeIndex - 1);
        return true;
      case "Home":
        if (!homeEnd) return false;
        e.preventDefault();
        if (count) activate(0);
        return true;
      case "End":
        if (!homeEnd) return false;
        e.preventDefault();
        if (count) activate(count - 1);
        return true;
      case "Enter":
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < count) onPick(activeIndex);
        return true;
      default: {
        if (e.key.length !== 1) return false;
        if (e.key === " " && !typeState.text) {
          if (!spaceSelects) return false;
          e.preventDefault();
          if (activeIndex >= 0 && activeIndex < count) onPick(activeIndex);
          return true;
        }
        if (!typeAhead) return false;
        e.preventDefault();
        clearTimeout(typeState.timer);
        typeState.text += e.key.toLowerCase();
        typeState.timer = setTimeout(() => {
          typeState.text = "";
        }, 700);
        const match = labels.findIndex((l) => String(l).toLowerCase().startsWith(typeState.text));
        if (match >= 0) activate(match);
        return true;
      }
    }
  };

  return { activeIndex, activate, listRef, handleNavKey };
}

/* ======================================================
   Shared toolbar dropdown — used by Year / Department so both
   render identically (rounded popover, flip arrow, hover states,
   scrollable-but-scrollbar-hidden list). A native <select> can't
   reproduce this consistently across browsers, so this replaces it
   everywhere in the toolbar (except the Company filter, which uses
   CompanySearchDropdown below so it can double as the search box).

   `options` is an array of { value, label }. `value` is the current
   selected value (string). `onChange(value)` fires with the raw value
   (not an event) when an option is picked.

   The whole pill (padding + icon + text + arrow) is clickable, and
   clicks on the list itself can't bubble up and re-toggle it.
   ====================================================== */
const CustomDropdown = ({
  icon,
  label,
  options,
  value,
  placeholder,
  onChange,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const listId = useDropdownUid("rep-emr-filter-list");

  const selectedIndex = options.findIndex((opt) => opt.value === value);
  const selectedLabel = selectedIndex >= 0 ? options[selectedIndex].label : placeholder;

  const focusTrigger = () => {
    if (triggerRef.current) triggerRef.current.focus();
  };

  const pick = (index) => {
    const opt = options[index];
    if (!opt) return;
    onChange(opt.value);
    setOpen(false);
    focusTrigger();
    setQuietFocus(triggerRef.current, true);
  };

  const nav = useDropdownNav({
    open,
    labels: options.map((o) => o.label),
    onPick: pick,
    typeAhead: true,
    spaceSelects: true,
  });

  const openList = () => {
    nav.activate(selectedIndex >= 0 ? selectedIndex : 0);
    setOpen(true);
    focusTrigger(); // Safari doesn't focus buttons on click
  };

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handlePillClick = (e) => {
    if (disabled) return;
    if (nav.listRef.current && nav.listRef.current.contains(e.target)) return;
    if (open) setOpen(false);
    else openList();
  };

  const handleKeyDown = (e) => {
    if (handleDropdownSideKey(e, triggerRef.current, open)) return;
    if (disabled) return;
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openList();
      } else if (isPrintableKey(e)) {
        e.preventDefault();
        openList();
        nav.handleNavKey(e);
      }
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      return;
    }
    if (e.key === "Tab") {
      setOpen(false);
      return;
    }
    nav.handleNavKey(e);
  };

  // Firefox fires the button "click" on Space keyup — stop that double toggle.
  const handleKeyUp = (e) => {
    if (e.key === " ") e.preventDefault();
  };

  const handleBlur = (e) => {
    setQuietFocus(triggerRef.current, false);
    if (ref.current && !ref.current.contains(e.relatedTarget)) setOpen(false);
  };

  return (
    <div
      className={`rep_emr-select-inline rep_emr-custom-dropdown${
        disabled ? " rep_emr-custom-dropdown-disabled" : ""
      }`}
      ref={ref}
      onClick={handlePillClick}
      onBlur={handleBlur}
    >
      <img src={icon} alt="" className="rep_emr-inline-icon-img" />
      <button
        type="button"
        ref={triggerRef}
        data-dd-nav="true"
        className="rep_emr-custom-dropdown-trigger"
        disabled={disabled}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={`${label}: ${selectedLabel}`}
        aria-activedescendant={open && nav.activeIndex >= 0 ? `${listId}-opt-${nav.activeIndex}` : undefined}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
      >
        <span>{selectedLabel}</span>
        <img
          src={ICON_ARROW_DOWN}
          alt=""
          className={`rep_emr-custom-dropdown-arrow${open ? " rep_emr-arrow-up" : ""}`}
        />
      </button>

      {open && !disabled && (
        <div
          className="rep_emr-custom-dropdown-list"
          id={listId}
          role="listbox"
          ref={nav.listRef}
          onMouseDown={(e) => e.preventDefault()}
        >
          {options.map((opt, i) => (
            <div
              key={`${opt.value}-${i}`}
              id={`${listId}-opt-${i}`}
              role="option"
              aria-selected={value === opt.value}
              data-nav-index={i}
              className={`rep_emr-custom-dropdown-option${
                value === opt.value ? " rep_emr-option-selected" : ""
              }${i === nav.activeIndex ? " rep_emr-option-active" : ""}`}
              onMouseEnter={() => nav.activate(i, false)}
              onClick={(e) => {
                e.stopPropagation();
                pick(i);
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ======================================================
   CompanySearchDropdown — the "All Companies" box doubles as the
   search field, exactly like the Analytics report page. There is NO
   separate search input: closed, the box shows the selected company
   (or the placeholder); clicked, it becomes a text input and the list
   underneath narrows as you type. "All Companies" always stays on
   top. Search text lives in here, so no searchValue/onSearchChange
   props are needed.

   FIX: the previous version had onClick={() => setOpen(true)} on the
   outer wrapper, which also contains the option list. Clicking an
   option closed the list, then that same click bubbled up to the
   wrapper and re-opened it. Clicks that start inside the list are now
   ignored by the pill.

   Same { value, label } option shape as CustomDropdown above.
   ====================================================== */
const CompanySearchDropdown = ({
  icon,
  options,
  value,
  placeholder,
  onChange,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const inputRef = useRef(null);
  const listId = useDropdownUid("rep-emr-company-list");

  const visibleOptions = filterCompanyOptions(options, search);
  const hasMatches = visibleOptions.some((o) => o.value !== "");
  const isSearching = normalizeSearchText(search) !== "";
  const selected = options.find((o) => o.value === value);
  const selectedLabel = selected ? selected.label : placeholder;

  const closeBox = (refocus) => {
    setOpen(false);
    setSearch("");
    if (refocus && triggerRef.current) triggerRef.current.focus();
  };

  const pick = (index) => {
    const opt = visibleOptions[index];
    if (!opt) return;
    onChange(opt.value);
    closeBox(true);
    setQuietFocus(triggerRef.current, true);
  };

  const nav = useDropdownNav({
    open,
    labels: visibleOptions.map((o) => o.label),
    onPick: pick,
    homeEnd: false, // Home/End must keep moving the caret inside the input
  });

  const openBox = () => {
    if (open || disabled) return;
    setSearch("");
    nav.activate(getCompanyActiveIndex(options, "", value));
    setOpen(true);
  };

  const handleSearchChange = (text) => {
    setSearch(text);
    nav.activate(getCompanyActiveIndex(options, text, value));
  };

  // Focus the input imperatively whenever the box opens, so typing
  // works right away wherever inside the box the click landed.
  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handlePillClick = (e) => {
    if (disabled) return;
    if (nav.listRef.current && nav.listRef.current.contains(e.target)) return;
    openBox();
  };

  const handleKeyDown = (e) => {
    if (handleDropdownSideKey(e, triggerRef.current, open)) return;
    if (disabled) return;
    if (!open) {
      if (e.target !== e.currentTarget) return;
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openBox();
      } else if (isPrintableKey(e)) {
        e.preventDefault();
        openBox();
        handleSearchChange(e.key);
      }
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      closeBox(true);
      return;
    }
    nav.handleNavKey(e); // Tab is left alone: blur below closes the box
  };

  const handleBlur = (e) => {
    setQuietFocus(triggerRef.current, false);
    if (ref.current && !ref.current.contains(e.relatedTarget)) closeBox(false);
  };

  const handleArrowClick = (e) => {
    e.stopPropagation();
    if (disabled) return;
    if (open) closeBox(true);
    else openBox();
  };

  return (
    <div
      className={`rep_emr-select-inline rep_emr-custom-dropdown rep_emr-company-search${
        disabled ? " rep_emr-custom-dropdown-disabled" : ""
      }`}
      ref={ref}
      onClick={handlePillClick}
      onBlur={handleBlur}
    >
      <img src={icon} alt="" className="rep_emr-inline-icon-img" />

      <div
        ref={triggerRef}
        data-dd-nav="true"
        className="rep_emr-custom-dropdown-trigger rep_emr-company-search-trigger"
        tabIndex={disabled ? -1 : 0}
        role={open ? undefined : "combobox"}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-disabled={disabled}
        aria-label="Company"
        onKeyDown={handleKeyDown}
      >
        {open && !disabled ? (
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-label="Search company"
            aria-autocomplete="list"
            aria-expanded="true"
            aria-controls={listId}
            aria-activedescendant={nav.activeIndex >= 0 ? `${listId}-opt-${nav.activeIndex}` : undefined}
            autoComplete="off"
            className="rep_emr-company-search-input"
            placeholder="Search Company..."
            value={search}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        ) : (
          <span>{selectedLabel}</span>
        )}
        <img
          src={ICON_ARROW_DOWN}
          alt=""
          className={`rep_emr-custom-dropdown-arrow${open ? " rep_emr-arrow-up" : ""}`}
          onClick={handleArrowClick}
        />
      </div>

      {open && !disabled && (
        <div
          className="rep_emr-custom-dropdown-list"
          id={listId}
          role="listbox"
          ref={nav.listRef}
          onMouseDown={(e) => e.preventDefault()}
        >
          {visibleOptions.map((opt, i) => (
            <div
              key={`${opt.value}-${i}`}
              id={`${listId}-opt-${i}`}
              role="option"
              aria-selected={value === opt.value}
              data-nav-index={i}
              className={`rep_emr-custom-dropdown-option${
                value === opt.value ? " rep_emr-option-selected" : ""
              }${i === nav.activeIndex ? " rep_emr-option-active" : ""}`}
              onMouseEnter={() => nav.activate(i, false)}
              onClick={(e) => {
                e.stopPropagation();
                pick(i);
              }}
            >
              {opt.label}
            </div>
          ))}
          {/* `options` always carries the "All Companies" entry, so the
              no-results note is keyed off the real companies only. */}
          {isSearching && !hasMatches && (
            <div className="rep_emr-company-search-empty">No companies found.</div>
          )}
        </div>
      )}
    </div>
  );
};

/* ======================================================
   Quiz filter dropdown — used only inside the Assessment Taken
   "View Details" sub-view to switch which quiz's questions are
   shown. Open-list style per Figma: plain rounded panel, generous
   vertical spacing, no bold "selected" background — kept separate
   from CustomDropdown above since that one's compact toolbar look
   is a different spec and shouldn't change. Same keyboard behaviour
   as the toolbar dropdowns.
   ====================================================== */
const QuizFilterDropdown = ({ options, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const listId = useDropdownUid("rep-emr-quiz-list");

  const selectedIndex = options.findIndex((opt) => opt.value === value);
  const selectedLabel = selectedIndex >= 0 ? options[selectedIndex].label : "Select Quiz";

  const pick = (index) => {
    const opt = options[index];
    if (!opt) return;
    onChange(opt.value);
    setOpen(false);
    if (triggerRef.current) triggerRef.current.focus();
    setQuietFocus(triggerRef.current, true);
  };

  const nav = useDropdownNav({
    open,
    labels: options.map((o) => o.label),
    onPick: pick,
    typeAhead: true,
    spaceSelects: true,
  });

  const openList = () => {
    nav.activate(selectedIndex >= 0 ? selectedIndex : 0);
    setOpen(true);
    if (triggerRef.current) triggerRef.current.focus();
  };

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleKeyDown = (e) => {
    if (handleDropdownSideKey(e, triggerRef.current, open)) return;
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openList();
      } else if (isPrintableKey(e)) {
        e.preventDefault();
        openList();
        nav.handleNavKey(e);
      }
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      return;
    }
    if (e.key === "Tab") {
      setOpen(false);
      return;
    }
    nav.handleNavKey(e);
  };

  const handleKeyUp = (e) => {
    if (e.key === " ") e.preventDefault();
  };

  const handleBlur = (e) => {
    setQuietFocus(triggerRef.current, false);
    if (ref.current && !ref.current.contains(e.relatedTarget)) setOpen(false);
  };

  return (
    <div className="rep_emr-quiz-dropdown" ref={ref} onBlur={handleBlur}>
      <button
        type="button"
        ref={triggerRef}
        data-dd-nav="true"
        className="rep_emr-quiz-dropdown-trigger"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={`Quiz: ${selectedLabel}`}
        aria-activedescendant={open && nav.activeIndex >= 0 ? `${listId}-opt-${nav.activeIndex}` : undefined}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
      >
        <img
          src={ICON_ASSESSMENT_FILTER}
          alt=""
          className="rep_emr-quiz-dropdown-icon"
        />
        <span>{selectedLabel}</span>
        <img
          src={ICON_ARROW_DOWN}
          alt=""
          className={`rep_emr-quiz-dropdown-arrow${open ? " rep_emr-arrow-up" : ""}`}
        />
      </button>

      {open && (
        <div
          className="rep_emr-quiz-dropdown-list"
          id={listId}
          role="listbox"
          ref={nav.listRef}
          onMouseDown={(e) => e.preventDefault()}
        >
          {options.map((opt, i) => (
            <div
              key={`${opt.value}-${i}`}
              id={`${listId}-opt-${i}`}
              role="option"
              aria-selected={value === opt.value}
              data-nav-index={i}
              className={`rep_emr-quiz-dropdown-option${
                value === opt.value ? " rep_emr-quiz-option-selected" : ""
              }${i === nav.activeIndex ? " rep_emr-quiz-option-active" : ""}`}
              onMouseEnter={() => nav.activate(i, false)}
              onClick={() => pick(i)}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------
   Session-scoped persistence for this report's filters — same pattern
   as the Analytics report page (see ReportPage.jsx /
   clearAnalyticsReportSession). sessionStorage is intentional: it's
   what makes a plain page refresh keep the selected year/company/
   department (and the "All Companies" toggle), while a closed tab (or
   an explicit call to clearEmployeeReportSession() from your logout
   action) starts the page fresh again. Call
   clearEmployeeReportSession() from wherever your app's logout action
   lives, the same way you already call clearAnalyticsReportSession().
   Import it as:
     import EmployeeReports, { clearEmployeeReportSession } from ".../EmployeeReport";

   Only the ADMIN view uses this. The customer view (lockedCompanyId)
   never reads or writes it, so it can't clobber the admin's saved filters.
   ------------------------------------------------------------------ */
const EMPLOYEE_REPORT_FILTERS_KEY = "employee-report-filters";
const CUSTOMER_EMPLOYEE_YEAR_KEY = "customer-employee-report-year";

// Adds native (editable) Excel doughnut charts to the first worksheet of an
// .xlsx produced by exceljs, by injecting DrawingML chart parts into the zip.
// charts: [{ title, catRef, valRef, cats[], vals[], colors[], hole, firstAngle,
//            hiddenIdx[], showLabels, legend, anchor:{c1,r1,c2,r2} }]
async function addNativeDoughnutCharts(JSZip, xlsxBuffer, charts) {
  const esc = (t) => String(t).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  const zip = await JSZip.loadAsync(xlsxBuffer);

  const chartXml = (ch) => {
    const dpts = ch.colors.map((col, i) =>
      `<c:dPt><c:idx val="${i}"/><c:bubble3D val="0"/><c:spPr>${
        (ch.hiddenIdx || []).includes(i)
          ? "<a:noFill/><a:ln><a:noFill/></a:ln>"
          : `<a:solidFill><a:srgbClr val="${col}"/></a:solidFill><a:ln w="19050"><a:solidFill><a:srgbClr val="FFFFFF"/></a:solidFill></a:ln>`
      }</c:spPr></c:dPt>`).join("");
    const dLbls = ch.showLabels
      ? `<c:dLbls><c:numFmt formatCode="0%;;;" sourceLinked="0"/><c:spPr><a:noFill/><a:ln><a:noFill/></a:ln></c:spPr><c:txPr><a:bodyPr/><a:lstStyle/><a:p><a:pPr><a:defRPr sz="1000" b="1"><a:solidFill><a:srgbClr val="FFFFFF"/></a:solidFill></a:defRPr></a:pPr><a:endParaRPr lang="en-US"/></a:p></c:txPr><c:showLegendKey val="0"/><c:showVal val="0"/><c:showCatName val="0"/><c:showSerName val="0"/><c:showPercent val="1"/><c:showBubbleSize val="0"/><c:showLeaderLines val="0"/></c:dLbls>`
      : `<c:dLbls><c:delete val="1"/></c:dLbls>`;
    const catPts = ch.cats.map((c, i) => `<c:pt idx="${i}"><c:v>${esc(c)}</c:v></c:pt>`).join("");
    const valPts = ch.vals.map((v, i) => `<c:pt idx="${i}"><c:v>${Number(v) || 0}</c:v></c:pt>`).join("");
    const legend = ch.legend
      ? `<c:legend><c:legendPos val="r"/><c:overlay val="0"/><c:txPr><a:bodyPr/><a:lstStyle/><a:p><a:pPr><a:defRPr sz="1100"/></a:pPr><a:endParaRPr lang="en-US"/></a:p></c:txPr></c:legend>`
      : "";
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<c:chartSpace xmlns:c="http://schemas.openxmlformats.org/drawingml/2006/chart" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><c:roundedCorners val="0"/><c:chart><c:title><c:tx><c:rich><a:bodyPr/><a:lstStyle/><a:p><a:pPr><a:defRPr sz="1400" b="1"/></a:pPr><a:r><a:rPr lang="en-US" sz="1400" b="1"/><a:t>${esc(ch.title)}</a:t></a:r></a:p></c:rich></c:tx><c:overlay val="0"/></c:title><c:autoTitleDeleted val="0"/><c:plotArea><c:layout/><c:doughnutChart><c:varyColors val="1"/><c:ser><c:idx val="0"/><c:order val="0"/><c:tx><c:v>${esc(ch.title)}</c:v></c:tx>${dpts}${dLbls}<c:cat><c:strRef><c:f>${esc(ch.catRef)}</c:f><c:strCache><c:ptCount val="${ch.cats.length}"/>${catPts}</c:strCache></c:strRef></c:cat><c:val><c:numRef><c:f>${esc(ch.valRef)}</c:f><c:numCache><c:formatCode>General</c:formatCode><c:ptCount val="${ch.vals.length}"/>${valPts}</c:numCache></c:numRef></c:val></c:ser><c:firstSliceAng val="${ch.firstAngle || 0}"/><c:holeSize val="${ch.hole || 65}"/></c:doughnutChart></c:plotArea>${legend}<c:plotVisOnly val="1"/><c:dispBlanksAs val="gap"/></c:chart><c:spPr><a:solidFill><a:srgbClr val="FFFFFF"/></a:solidFill><a:ln w="9525"><a:solidFill><a:srgbClr val="D7DEF0"/></a:solidFill></a:ln></c:spPr></c:chartSpace>`;
  };

  const anchors = charts.map((ch, i) => {
    const a = ch.anchor;
    return `<xdr:twoCellAnchor><xdr:from><xdr:col>${a.c1}</xdr:col><xdr:colOff>0</xdr:colOff><xdr:row>${a.r1}</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:from><xdr:to><xdr:col>${a.c2}</xdr:col><xdr:colOff>0</xdr:colOff><xdr:row>${a.r2}</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:to><xdr:graphicFrame macro=""><xdr:nvGraphicFramePr><xdr:cNvPr id="${i + 2}" name="Chart ${i + 1}"/><xdr:cNvGraphicFramePr/></xdr:nvGraphicFramePr><xdr:xfrm><a:off x="0" y="0"/><a:ext cx="0" cy="0"/></xdr:xfrm><a:graphic><a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/chart"><c:chart xmlns:c="http://schemas.openxmlformats.org/drawingml/2006/chart" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" r:id="rId${i + 1}"/></a:graphicData></a:graphic></xdr:graphicFrame><xdr:clientData/></xdr:twoCellAnchor>`;
  }).join("");

  zip.file("xl/drawings/drawing1.xml", `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<xdr:wsDr xmlns:xdr="http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main">${anchors}</xdr:wsDr>`);
  zip.file("xl/drawings/_rels/drawing1.xml.rels", `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${charts.map((_, i) => `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/chart" Target="../charts/chart${i + 1}.xml"/>`).join("")}</Relationships>`);
  charts.forEach((ch, i) => zip.file(`xl/charts/chart${i + 1}.xml`, chartXml(ch)));

  // sheet1 -> drawing relationship
  const relsPath = "xl/worksheets/_rels/sheet1.xml.rels";
  const drawRel = `<Relationship Id="rIdNativeCharts" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/drawing" Target="../drawings/drawing1.xml"/>`;
  const existing = zip.file(relsPath);
  if (existing) {
    const txt = await existing.async("string");
    zip.file(relsPath, txt.replace("</Relationships>", drawRel + "</Relationships>"));
  } else {
    zip.file(relsPath, `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${drawRel}</Relationships>`);
  }

  // <drawing> element in the sheet (must come after page setup, before tableParts)
  const sheetPath = "xl/worksheets/sheet1.xml";
  let sheet = await zip.file(sheetPath).async("string");
  if (!/xmlns:r=/.test(sheet.slice(0, 600))) {
    sheet = sheet.replace("<worksheet ", '<worksheet xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" ');
  }
  const drawingTag = '<drawing r:id="rIdNativeCharts"/>';
  if (/<tableParts|<legacyDrawing|<extLst/.test(sheet)) {
    sheet = sheet.replace(/(<tableParts|<legacyDrawing|<extLst)/, drawingTag + "$1");
  } else {
    sheet = sheet.replace("</worksheet>", drawingTag + "</worksheet>");
  }
  zip.file(sheetPath, sheet);

  // content types
  let ct = await zip.file("[Content_Types].xml").async("string");
  const add =
    `<Override PartName="/xl/drawings/drawing1.xml" ContentType="application/vnd.openxmlformats-officedocument.drawing+xml"/>` +
    charts.map((_, i) => `<Override PartName="/xl/charts/chart${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.drawingml.chart+xml"/>`).join("");
  ct = ct.replace("</Types>", add + "</Types>");
  zip.file("[Content_Types].xml", ct);

  return zip.generateAsync({ type: "arraybuffer", compression: "DEFLATE" });
}

export function clearEmployeeReportSession() {
  try {
    sessionStorage.removeItem(EMPLOYEE_REPORT_FILTERS_KEY);
  } catch {
    // sessionStorage unavailable (private browsing, locked-down
    // context) — nothing was persisted in the first place.
  }
}

const EmployeeReports = ({
  embedded = false,
  lockedCompanyId = null,
  yearRecords = [],
  onYearChange = null, // no longer needed: the customer year filter now lives inside this component
}) => {
  // Customer view = a company is locked in (opened from the customer dashboard).
  const isCustomerView = !!lockedCompanyId;

  const readSavedFilters = () => {
    if (isCustomerView) return {};
    try {
      return JSON.parse(sessionStorage.getItem(EMPLOYEE_REPORT_FILTERS_KEY) || "{}");
    } catch {
      return {};
    }
  };
  const savedFilters = readSavedFilters();

  const [companies, setCompanies] = useState([]);
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(() => savedFilters.selectedYear || "");
  const [selectedCompany, setSelectedCompany] = useState(() => savedFilters.selectedCompany || "");
  // Customer-only year filter. Default = "All Years" (every year combined).
 const [customerYear, setCustomerYear] = useState(() => {
  if (!lockedCompanyId) return ALL_YEARS;
  try {
    return sessionStorage.getItem(CUSTOMER_EMPLOYEE_YEAR_KEY) || ALL_YEARS;
  } catch {
    return ALL_YEARS;
  }
});
  const [employees, setEmployees] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(() => savedFilters.selectedDepartment || "");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalEmployee, setModalEmployee] = useState({
    id: null,
    company_id: "",
    name: "",
    email: "",
    department: "",
    gender: "Male",
  });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteEmployeeId, setDeleteEmployeeId] = useState(null);
  const [successAlert, setSuccessAlert] = useState("");
  const [errorAlert, setErrorAlert] = useState("");
  const [showAll, setShowAll] = useState(() => savedFilters.showAll || false);
  const [hoveredLevel, setHoveredLevel] = useState(null);
  // Drives the donut "draw-in" animation: segments render at 0 length on
  // mount / whenever the selected company (and therefore its data)
  // changes, then flip to their real length a tick later so the CSS
  // transition on stroke-dasharray/stroke-dashoffset actually animates
  // instead of popping straight to the final state.
  const [chartAnimated, setChartAnimated] = useState(false);
  // Which sections have scrolled into view (drives the enter animations).
  const revealed = useScrollReveal(`${selectedCompany}|${showAll}`);
  // Real Likert answer counts (Strongly Agree/Agree/Neutral/Disagree/
  // Strongly Disagree) for the selected company, fetched from the
  // backend. Falls back to EMPTY_RESPONSE_COUNTS (all zeros) in responseSummary
  // below when null/empty (admin view only).
  const [companyResponseCounts, setCompanyResponseCounts] = useState(null);

  // Assessment Taken sub-view — toggled in-page by the "Assessment
  // Report" button instead of navigating to a separate route/page.
  const [showAssessment, setShowAssessment] = useState(false);
  const [assessmentLoading, setAssessmentLoading] = useState(false);
  const [assessmentError, setAssessmentError] = useState("");
  const [assessmentEmployee, setAssessmentEmployee] = useState(null);
  const [assessmentQuizzes, setAssessmentQuizzes] = useState([]);
  const [assessmentSubView, setAssessmentSubView] = useState("list"); // "list" | "detail"
  const [selectedQuizId, setSelectedQuizId] = useState(null);
  const [employeePage, setEmployeePage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(20);
  const [tableSearch, setTableSearch] = useState("");
  useEffect(() => {
    setEmployeePage(1);
  }, [tableSearch, entriesPerPage]);

  // Request counters: a slow, older response can never overwrite a newer
  // one (or repopulate the list after "All Companies" cleared it).
  const employeesRequestRef = useRef(0);
  const summaryRequestRef = useRef(0);

  // The person clicks "Assessment Report" (or "View Details" / "Back")
  // from wherever they've scrolled to in a long table — without this,
  // the newly-shown content would just appear at that same scroll
  // position instead of starting from the top of the page.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [showAssessment, assessmentSubView]);

  const location = useLocation();

  // Persist the admin filters to sessionStorage — this + the saved-filter
  // reads above are what make a plain page refresh keep the same year/
  // company/department/"All Companies" state. Logging out should call
  // clearEmployeeReportSession() instead, so the page starts fresh on
  // the next login. Skipped entirely in the customer view.
  useEffect(() => {
    if (isCustomerView) return;
    try {
      sessionStorage.setItem(
        EMPLOYEE_REPORT_FILTERS_KEY,
        JSON.stringify({ selectedYear, selectedCompany, selectedDepartment, showAll })
      );
    } catch {
      // sessionStorage can throw in private-browsing/locked-down
      // contexts — filters just won't persist there, nothing to fix.
    }
  }, [isCustomerView, selectedYear, selectedCompany, selectedDepartment, showAll]);
  // Customer view: remember the selected year across a page refresh.
useEffect(() => {
  if (!isCustomerView) return;
  try {
    sessionStorage.setItem(CUSTOMER_EMPLOYEE_YEAR_KEY, String(customerYear));
  } catch {}
}, [isCustomerView, customerYear]);

// Customer view: if the saved year no longer exists, fall back to All Years.
useEffect(() => {
  if (!isCustomerView || customerYear === ALL_YEARS || yearRecords.length === 0) return;
  const exists = yearRecords.some((r) => String(r.id) === String(customerYear));
  if (!exists) setCustomerYear(ALL_YEARS);
}, [isCustomerView, yearRecords, customerYear]);

  // Fetch companies on mount. NOTE: this no longer forces
  // selectedCompany/employees/filtered back to empty — that reset was
  // undoing the session-restored selection above on every page load.
  useEffect(() => {
    fetchCompanies();
    // eslint-disable-next-line
  }, []);

  // Derive year list once companies are loaded
  useEffect(() => {
    if (companies.length > 0) {
      const uniqueYears = [...new Set(companies.map((c) => c.year))].sort(
        (a, b) => b - a
      );
      setYears(uniqueYears);
    } else {
      setYears([]);
    }
  }, [companies]);

  // After companies are loaded, either lock onto the customer's own
  // company (lockedCompanyId, passed in from the customer dashboard)
  // or, for admins, honour an explicit ?company_id in the URL. With no
  // company_id in the URL this intentionally does nothing — the
  // session-restored (or default) selection is left alone instead of
  // being reset to "" on every page load/refresh.
  useEffect(() => {
    if (companies.length === 0) return;

    if (lockedCompanyId) {
      const id = lockedCompanyId.toString();
      if (companies.some((c) => c.id.toString() === id)) {
        setSelectedCompany(id);
      }
      return;
    }

    const params = new URLSearchParams(location.search);
    const companyId = params.get("company_id");
    if (companyId && companies.some((c) => c.id.toString() === companyId)) {
      const matchedCompany = companies.find((c) => c.id.toString() === companyId);
      if (matchedCompany) setSelectedYear(matchedCompany.year.toString());
      setShowAll(false);
      setSelectedCompany(companyId);
      fetchEmployees(companyId);
    }
    // eslint-disable-next-line
  }, [companies, location.search, lockedCompanyId]);

  // Normalize string: remove spaces, special chars, lowercase. Used for
  // matching the selected department filter against each employee's
  // stored department string, so differences in spacing/casing/
  // punctuation between the two don't cause a false "no match".
  const normalize = (str) => (str || "").toLowerCase().replace(/[^a-z0-9]/gi, "");

  // Company list narrowed by the selected year only — the text search now
  // lives inside CompanySearchDropdown itself.
  const filteredCompanies = companies.filter(
    (c) => !selectedYear || c.year?.toString() === selectedYear
  );

  // ── CUSTOMER VIEW: which company records (years) are being shown ──
  // "All Years" => every year record of this customer; otherwise just
  // the one picked. Records come from the parent as `yearRecords`.
  const customerTargetIds = useMemo(() => {
    if (!isCustomerView) return [];
    const recs =
      customerYear === ALL_YEARS
        ? yearRecords
        : yearRecords.filter((r) => String(r.id) === String(customerYear));
    if (!recs.length) return [String(lockedCompanyId)];
    return recs.map((r) => String(r.id));
  }, [isCustomerView, customerYear, yearRecords, lockedCompanyId]);

  // ── CUSTOMER VIEW: load employees + response summary for every
  // selected year, then combine them into one list / one set of counts.
  useEffect(() => {
    if (!isCustomerView || !customerTargetIds.length) return;
    let cancelled = false;

    (async () => {
      const empResults = await Promise.all(
        customerTargetIds.map((id) =>
          axios
            .get(`${API_BASE_URL}/api/employees?company_id=${id}`)
            .then((r) => r.data || [])
            .catch(() => [])
        )
      );
      const summaryResults = await Promise.all(
        customerTargetIds.map((id) =>
          axios
            .get(`${API_BASE_URL}/api/employees/response-summary?company_id=${id}`)
            .then((r) => r.data)
            .catch(() => null)
        )
      );
      if (cancelled) return;

      // Newest year first (customerTargetIds follows yearRecords order).
      const allEmployees = empResults.flat();
      setEmployees(allEmployees);
      setFiltered(allEmployees);

      // Add the Likert counts of every year together.
      const merged = { ...EMPTY_RESPONSE_COUNTS };
      summaryResults.forEach((s) => {
        if (!s) return;
        Object.keys(merged).forEach((k) => {
          merged[k] += Number(s[k]) || 0;
        });
      });
      setCompanyResponseCounts(merged);
    })();

    return () => {
      cancelled = true;
    };
  }, [isCustomerView, customerTargetIds]);

  // ADMIN view data loading (customer view is handled by the effect above).
  useEffect(() => {
    if (isCustomerView) return;
    if (showAll) {
      fetchEmployees();
      fetchResponseSummary();
    } else if (selectedCompany) {
      fetchEmployees(selectedCompany);
      fetchResponseSummary(selectedCompany);
    } else {
      // cancel any request still in flight, then clear
      employeesRequestRef.current += 1;
      summaryRequestRef.current += 1;
      setEmployees([]);
      setFiltered([]);
      setCompanyResponseCounts(null);
    }
    // eslint-disable-next-line
  }, [selectedCompany, showAll]);

  // Re-apply the department filter whenever the base employee list or
  // the department filter changes. (The old name/email text filter went
  // away with the standalone search box — search now lives inside the
  // All Companies dropdown and only narrows that list.)
  //
  // Comparing through the same `normalize()` makes the department match
  // tolerant of spacing/casing/punctuation differences.
  useEffect(() => {
    let next = employees;
    if (selectedDepartment) {
      const targetDept = normalize(selectedDepartment);
      next = next.filter((emp) => normalize(emp.department) === targetDept);
    }
    setFiltered(next);
    setEmployeePage(1);
    // eslint-disable-next-line
  }, [employees, selectedDepartment]);

  const fetchCompanies = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/employees/companies`);
      setCompanies(res.data);
    } catch (err) {
      setCompanies([]);
    }
  };

  const fetchEmployees = async (company_id) => {
    const reqId = ++employeesRequestRef.current;
    try {
      let url = `${API_BASE_URL}/api/employees`;
      if (!showAll && company_id) url += `?company_id=${company_id}`;
      const res = await axios.get(url);
      if (reqId !== employeesRequestRef.current) return;
      setEmployees(res.data);
      setFiltered(res.data);
    } catch (err) {
      if (reqId !== employeesRequestRef.current) return;
      setEmployees([]);
      setFiltered([]);
    }
  };

  // Fetches real Likert answer counts for the "Response Summary
  // (Overall)" donut — for the selected company, or aggregated across
  // all companies when showAll is active.
  const fetchResponseSummary = async (company_id) => {
    const reqId = ++summaryRequestRef.current;
    try {
      let url = `${API_BASE_URL}/api/employees/response-summary`;
      if (!showAll && company_id) url += `?company_id=${company_id}`;
      const res = await axios.get(url);
      if (reqId !== summaryRequestRef.current) return;
      setCompanyResponseCounts(res.data);
    } catch (err) {
      if (reqId !== summaryRequestRef.current) return;
      setCompanyResponseCounts(null);
    }
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
    setSelectedCompany("");
    setSelectedDepartment("");
    setEmployees([]);
    setFiltered([]);
  };

  // No direct fetchEmployees() here — the effect on
  // [selectedCompany, showAll] already loads the right data. A direct
  // call ran fetchEmployees("") for "All Companies", which loads EVERY
  // employee and raced with the effect that clears the list.
  const handleCompanyChange = (companyId) => {
    setShowAll(false);
    setSelectedCompany(companyId);
  };

  const openEditModal = (emp) => {
    setModalEmployee({
      id: emp.id,
      company_id: emp.company_id,
      name: emp.name,
      email: emp.email,
      department: emp.department,
      gender: emp.gender,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalEmployee({
      id: null,
      company_id: selectedCompany,
      name: "",
      email: "",
      department: "",
      gender: "Male",
    });
  };

  const handleModalChange = (e) => {
    setModalEmployee({ ...modalEmployee, [e.target.name]: e.target.value });
  };

  const handleModalSave = async () => {
    if (
      !modalEmployee.name.trim() ||
      !modalEmployee.email.trim() ||
      !modalEmployee.company_id
    )
      return;
    try {
      await axios.post(
        `${API_BASE_URL}/api/employees/edit/${modalEmployee.id}`,
        modalEmployee
      );
      setSuccessAlert("Employee updated successfully!");
      closeModal();
      fetchEmployees(showAll ? undefined : selectedCompany);
      setTimeout(() => setSuccessAlert(""), 2000);
    } catch (err) {
      setErrorAlert("Error saving employee!");
      setTimeout(() => setErrorAlert(""), 2000);
    }
  };

  const openDeleteModal = (id) => {
    setDeleteEmployeeId(id);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteEmployeeId(null);
    setDeleteModalOpen(false);
  };

  const confirmDelete = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/employees/delete/${deleteEmployeeId}`);
      closeDeleteModal();
      fetchEmployees(showAll ? undefined : selectedCompany);
      setErrorAlert("Employee deleted successfully!");
      setTimeout(() => setErrorAlert(""), 2000);
    } catch (err) {
      setErrorAlert("Error deleting employee!");
      setTimeout(() => setErrorAlert(""), 2000);
    }
  };

  // Loads THIS employee's own assessment report from the two real
  // backend endpoints:
  //   - GET /api/assessment-taken?employee_email=...  -> one row per
  //     completed section (success / pass_score / total_score)
  //   - GET /api/assessment-answers?employee_email=... -> every individual
  //     question + this employee's own selected answer, with section info
  // These are merged client-side into the { quizzes: [...] } shape the
  // rest of this view already expects.
  const handleAssessmentReport = async (emp) => {
    setAssessmentEmployee({
      name: emp.name,
      email: emp.email,
      company_name: emp.company_name,
    });
    setAssessmentQuizzes([]);
    setAssessmentSubView("list");
    setSelectedQuizId(null);
    setAssessmentError("");
    setAssessmentLoading(true);
    setShowAssessment(true);
    try {
      const [takenRes, answersRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/assessment-taken`, {
          params: { employee_email: emp.email },
        }),
        axios.get(`${API_BASE_URL}/api/assessment-answers`, {
          params: { employee_email: emp.email },
        }),
      ]);

      const takenRows = takenRes.data || [];
      const answerRows = answersRes.data?.answers || [];

      if (takenRows.length > 0) {
        setAssessmentEmployee({
          name: takenRows[0].employee_name || emp.name,
          email: emp.email,
          company_name: takenRows[0].company_name || emp.company_name,
        });
      }

      const quizzesBySection = {};
      takenRows.forEach((row) => {
        quizzesBySection[row.section] = {
          id: row.section,
          name: row.section,
          success: row.success === "Yes",
          pass_score: row.pass_score,
          total_score: row.total_score,
          questions: [],
        };
      });

      answerRows.forEach((row) => {
        const quiz = quizzesBySection[row.section];
        if (quiz) {
          quiz.questions.push({
            id: row.question_id,
            text: row.question,
            selected_answer: mapAnswerToKey(row.answer),
          });
        }
      });

     // Show whatever sections the employee actually took, in the order taken
const orderedQuizzes = [...takenRows]
  .sort((a, b) => a.id - b.id)
  .map((row) => quizzesBySection[row.section])
  .filter((quiz, i, arr) => quiz && arr.indexOf(quiz) === i);

      setAssessmentQuizzes(orderedQuizzes);
    } catch (err) {
      console.error("Failed to load assessment report:", err);
      setAssessmentError("Unable to load this employee's assessment report.");
    } finally {
      setAssessmentLoading(false);
    }
  };

  // Question numbering runs continuously across every quiz (01–06 for
  // quiz 1, 07–13 for quiz 2, ...) rather than resetting per quiz.
  const assessmentQuizzesWithOffsets = useMemo(() => {
    let running = 0;
    return assessmentQuizzes.map((q) => {
      const offset = running;
      running += (q.questions || []).length;
      return { ...q, questionOffset: offset };
    });
  }, [assessmentQuizzes]);

  const selectedAssessmentQuiz =
    assessmentQuizzesWithOffsets.find((q) => q.id === selectedQuizId) || null;

  const openAssessmentDetails = (quizId) => {
    setSelectedQuizId(quizId);
    setAssessmentSubView("detail");
  };

  const backToAssessmentList = () => setAssessmentSubView("list");

  const backToEmployees = () => setShowAssessment(false);

  // Export Excel Report: a real .xlsx with
  //   Sheet 1 "Summary"   – the numbers + native, editable Excel doughnut
  //                         charts (Response Rate gauge + Response Summary).
  //   Sheet 2 "Employees" – the currently filtered employee rows.
  const [exportingExcel, setExportingExcel] = useState(false);

  const handleExportExcel = async () => {
    if (filtered.length === 0 || exportingExcel) return;
    setExportingExcel(true);
    try {
      // Browser bundle of exceljs (the default "exceljs" entry targets Node and
      // breaks under webpack/CRA). Install once:  npm install exceljs
      const ExcelJSModule = await import("exceljs/dist/exceljs.min.js");
      const ExcelJS = ExcelJSModule.default || ExcelJSModule;
      const wb = new ExcelJS.Workbook();
      const NAVY = "FF00123F";
      const headFont = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
      const headFill = { type: "pattern", pattern: "solid", fgColor: { argb: NAVY } };
      const thin = { style: "thin", color: { argb: "FFD7DEF0" } };
      const box = { top: thin, left: thin, bottom: thin, right: thin };

      /* ---------- Sheet 1: Summary (numbers + native Excel charts) ---------- */
      const ws1 = wb.addWorksheet("Summary", { views: [{ showGridLines: false }] });
      ws1.columns = [{ width: 38 }, { width: 18 }, { width: 14 }, { width: 4 }];

      const companyLabel = showAll ? "All Companies" : selectedCompanyObj?.name || "Employees";
      ws1.getCell("A1").value = `Employee Report – ${companyLabel}`;
      ws1.getCell("A1").font = { bold: true, size: 16, color: { argb: NAVY } };

      const sectionTitle = (r, text) => {
        const c = ws1.getCell(`A${r}`);
        c.value = text;
        c.font = { bold: true, size: 13, color: { argb: NAVY } };
      };
      const headerRow = (r, labels) => {
        labels.forEach((t, i) => {
          const c = ws1.getCell(r, i + 1);
          c.value = t;
          c.font = headFont;
          c.fill = headFill;
          c.alignment = { horizontal: i === 0 ? "left" : "center", vertical: "middle" };
          c.border = box;
        });
      };
      const dataRow = (r, vals, bold = false) => {
        vals.forEach((v, i) => {
          const c = ws1.getCell(r, i + 1);
          c.value = v;
          c.border = box;
          c.alignment = { horizontal: i === 0 ? "left" : "center" };
          if (bold) c.font = { bold: true };
        });
      };

      // Rows 3-9: Participation & Response Rate
      sectionTitle(3, "Participation & Response Rate");
      headerRow(4, ["Metric", "Value"]);
      [
        ["Total Employees", totalHeadcount || "N/A"],
        ["Total Response", completedCount],
        ["Minimum Required (60%)", minimumRequired],
        ["Response Rate", `${responseRate}%`],
        ["Participation Status", responseRate >= 60 ? "Excellent Participation" : "Needs Follow-up"],
      ].forEach((r, i) => dataRow(5 + i, r));

      // Rows 12-21: Response Summary (chart source = rows 14-18)
      sectionTitle(12, "Response Summary ( Overall )");
      headerRow(13, ["Response", "Count", "Percent"]);
      responseSummary.levels.forEach((lvl, i) => {
        dataRow(14 + i, [lvl.label, lvl.count, `${lvl.percent}%`]);
        ws1.getCell(14 + i, 1).font = {
          bold: true,
          color: { argb: "FF" + String(lvl.color).replace("#", "").toUpperCase() },
        };
      });
      dataRow(19, ["Total Responses", responseSummary.total, "100%"], true);
      dataRow(20, ["Positive (Strongly Agree + Agree)", "", `${responseSummary.positivePercent}%`], true);
      dataRow(21, ["Negative (Disagree + Strongly Disagree)", "", `${responseSummary.negativePercent}%`], true);

      // Rows 24-28: data behind the Response Rate gauge chart
      sectionTitle(24, "Response Rate chart data");
      headerRow(25, ["Slice", "Value (%)"]);
      dataRow(26, ["Responded", responseRate]);
      dataRow(27, ["Remaining", 100 - responseRate]);
      dataRow(28, ["Gauge base (hidden half)", 100]);

      /* ---------- Sheet 2: Employees ---------- */
      const ws2 = wb.addWorksheet("Employees", { views: [{ state: "frozen", ySplit: 1 }] });
      const cols = [
        ["S.No", 8], ["Company", 32], ["Assessment Year", 16], ["Employee Name", 28],
        ["Email ID", 34], ["Department", 26], ["Gender", 12], ["Assessment Status", 18], ["Progress", 12],
      ];
      ws2.columns = cols.map(([header, width]) => ({ header, width }));
      ws2.getRow(1).eachCell((c) => {
        c.font = headFont;
        c.fill = headFill;
        c.alignment = { horizontal: "center", vertical: "middle" };
        c.border = box;
      });
      filtered.forEach((e, idx) => {
        const { status, progress } = getAssessmentProgress(e);
        const r = ws2.addRow([idx + 1, e.company_name, e.company_year, e.name, e.email, e.department, e.gender, status, `${progress}%`]);
        r.eachCell((c) => { c.border = box; });
      });

      const plainBuf = await wb.xlsx.writeBuffer();

      // Inject native Excel doughnut charts into the Summary sheet.
      const JSZipModule = await import("jszip");
      const JSZip = JSZipModule.default || JSZipModule;
      const strip = (c) => String(c).replace("#", "").toUpperCase();
      const buf = await addNativeDoughnutCharts(JSZip, plainBuf, [
        {
          title: "Response Rate: " + responseRate + "%",
          catRef: "Summary!$A$26:$A$28",
          valRef: "Summary!$B$26:$B$28",
          cats: ["Responded", "Remaining", "Gauge base"],
          vals: [responseRate, 100 - responseRate, 100],
          colors: ["00A631", "E5E7EB", "FFFFFF"],
          hiddenIdx: [2],
          firstAngle: 270,
          hole: 70,
          showLabels: false,
          legend: false,
          anchor: { c1: 4, r1: 2, c2: 11, r2: 19 },
        },
        {
          title: "Response Summary ( Overall )",
          catRef: "Summary!$A$14:$A$18",
          valRef: "Summary!$B$14:$B$18",
          cats: responseSummary.levels.map((l) => l.label),
          vals: responseSummary.levels.map((l) => l.count),
          colors: responseSummary.levels.map((l) => strip(l.color)),
          hole: 62,
          showLabels: true,
          legend: true,
          anchor: { c1: 12, r1: 2, c2: 21, r2: 19 },
        },
      ]);
      const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "employee_report.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Excel export (with charts) failed:", err);
      // Fallback so the button ALWAYS downloads something: plain .xlsx with
      // the summary numbers + employee rows (no chart picture).
      try {
        const XLSX = await import("xlsx");
        const summary = [
          ["Participation & Response Rate"],
          ["Total Employees", totalHeadcount || "N/A"],
          ["Total Response", completedCount],
          ["Minimum Required (60%)", minimumRequired],
          ["Response Rate", `${responseRate}%`],
          [],
          ["Response Summary ( Overall )"],
          ["Response", "Count", "Percent"],
          ...responseSummary.levels.map((l) => [l.label, l.count, `${l.percent}%`]),
          ["Total Responses", responseSummary.total, "100%"],
          ["Positive (Strongly Agree + Agree)", "", `${responseSummary.positivePercent}%`],
          ["Negative (Disagree + Strongly Disagree)", "", `${responseSummary.negativePercent}%`],
        ];
        const emp = [
          ["S.No", "Company", "Assessment Year", "Employee Name", "Email ID", "Department", "Gender", "Assessment Status", "Progress"],
          ...filtered.map((e, idx) => {
            const { status, progress } = getAssessmentProgress(e);
            return [idx + 1, e.company_name, e.company_year, e.name, e.email, e.department, e.gender, status, `${progress}%`];
          }),
        ];
        const wb2 = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb2, XLSX.utils.aoa_to_sheet(summary), "Summary");
        XLSX.utils.book_append_sheet(wb2, XLSX.utils.aoa_to_sheet(emp), "Employees");
        XLSX.writeFile(wb2, "employee_report.xlsx");
      } catch (fallbackErr) {
        console.error("Fallback export failed:", fallbackErr);
        window.alert("Could not export the Excel report: " + (fallbackErr?.message || err?.message || "unknown error"));
      }
    } finally {
      setExportingExcel(false);
    }
  };

  const hasSelection = showAll || !!selectedCompany;

  // Show the "Assessment Year" column for admins always, and for
  // customers only when "All Years" is selected (so each row's year is clear).
  const showYearCol = !isCustomerView || customerYear === ALL_YEARS;
  const tableColSpan = 8 + (showYearCol ? 1 : 0) + (isCustomerView ? 0 : 2);

  const EMPLOYEES_PAGE_SIZE = isCustomerView ? entriesPerPage : 20;

  // Customer view: search by name or email inside the table
  const searchedEmployees =
    isCustomerView && tableSearch.trim()
      ? filtered.filter((e) => {
          const q = tableSearch.trim().toLowerCase();
          return (
            (e.name || "").toLowerCase().includes(q) ||
            (e.email || "").toLowerCase().includes(q)
          );
        })
      : filtered;

  const totalEmployeePages = Math.max(
    1,
    Math.ceil(searchedEmployees.length / EMPLOYEES_PAGE_SIZE)
  );
  const paginatedEmployees = searchedEmployees.slice(
    (employeePage - 1) * EMPLOYEES_PAGE_SIZE,
    employeePage * EMPLOYEES_PAGE_SIZE
  );

  const goToEmployeePage = (page) => {
    setEmployeePage(Math.min(Math.max(page, 1), totalEmployeePages));
  };

  const employeeRangeStart =
    searchedEmployees.length === 0
      ? 0
      : (employeePage - 1) * EMPLOYEES_PAGE_SIZE + 1;
  const employeeRangeEnd = Math.min(
    employeePage * EMPLOYEES_PAGE_SIZE,
    searchedEmployees.length
  );

  const selectedCompanyObj = companies.find(
    (c) => c.id.toString() === selectedCompany
  );

  // Completed / In-progress counts across the currently filtered set —
  // used by the top stat cards AND by the "Total Response" figure in the
  // Participation & Response Rate card (which counts only fully
  // completed assessments, not every employee who has merely registered).
  const { completedCount, inProgressCount } = useMemo(() => {
    let completed = 0;
    let inProgress = 0;
    filtered.forEach((emp) => {
      const { status } = getAssessmentProgress(emp);
      if (status === "Completed") {
        completed += 1;
      } else {
        // "In Progress" and "Not Started" are both counted here since
        // there's no separate "Not Started" stat card in this row.
        inProgress += 1;
      }
    });
    return { completedCount: completed, inProgressCount: inProgress };
  }, [filtered]);

  // Headcount:
  //  - Customer view: sum of employees_count of every selected year
  //    ("All Years" = all years added together, one year = just that year).
  //  - Admin view: the selected company's employees_count.
  const totalHeadcount = isCustomerView
    ? companies
        .filter((c) => customerTargetIds.includes(String(c.id)))
        .reduce((sum, c) => sum + (Number(c.employees_count) || 0), 0)
    : selectedCompanyObj?.employees_count || 0;

  const responseRate =
    totalHeadcount > 0
      ? Math.min(100, Math.round((completedCount / totalHeadcount) * 100))
      : 0;
  const minimumRequired =
    totalHeadcount > 0 ? Math.round(totalHeadcount * 0.6) : "N/A";

  // Reads REAL Likert answer counts (fetched via fetchResponseSummary)
  // instead of a company field that never existed on the backend
  // response. When nobody has answered yet it shows zeros (no fake data).
  const responseSummary = useMemo(() => {
    const hasRealData =
      !!companyResponseCounts &&
      Object.values(companyResponseCounts).some((v) => Number(v) > 0);
    const counts = hasRealData ? companyResponseCounts : EMPTY_RESPONSE_COUNTS;

    const total =
      (counts.strongly_agree || 0) +
      (counts.agree || 0) +
      (counts.neutral || 0) +
      (counts.disagree || 0) +
      (counts.strongly_disagree || 0);
    const levels = RESPONSE_LEVELS.map((lvl) => {
      const count = counts[lvl.key] || 0;
      return {
        ...lvl,
        count,
        // `percent` is the rounded, human-readable label. `fraction` is
        // the exact, unrounded share used to draw the ring so five
        // rounded percentages that don't sum to exactly 100 never leave
        // a visible gap in the arc.
        percent: total > 0 ? Math.round((count / total) * 100) : 0,
        fraction: total > 0 ? count / total : 0,
      };
    });

    // Derive Positive/Negative % from the SAME already-rounded per-level
    // percentages shown in the legend, instead of rounding the combined
    // counts separately — otherwise two independently-rounded numbers
    // can visually disagree.
    const strongAgreeLevel = levels.find((l) => l.key === "strongly_agree");
    const agreeLevel = levels.find((l) => l.key === "agree");
    const disagreeLevel = levels.find((l) => l.key === "disagree");
    const strongDisagreeLevel = levels.find((l) => l.key === "strongly_disagree");

    const positivePercent =
      (strongAgreeLevel?.percent || 0) + (agreeLevel?.percent || 0);
    const negativePercent =
      (disagreeLevel?.percent || 0) + (strongDisagreeLevel?.percent || 0);

    return {
      levels,
      total,
      isDummy: !hasRealData,
      positivePercent,
      negativePercent,
    };
  }, [companyResponseCounts]);

  // Re-trigger the donut draw-in animation whenever the underlying
  // response data changes (company switch, or dummy->real data swap).
  useEffect(() => {
    setChartAnimated(false);
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setChartAnimated(true));
    });
    return () => cancelAnimationFrame(id);
  }, [responseSummary.total, responseSummary.isDummy]);

  // Option lists for the shared CustomDropdown, built from live state.
  const yearOptions = [
    { value: "", label: "All Years" },
    ...years.map((y) => ({ value: y.toString(), label: y.toString() })),
  ];

  const customerYearOptions = [
    { value: ALL_YEARS, label: "All Years" },
    ...yearRecords.map((r) => ({
      value: String(r.id),
      label: r.year ? String(r.year) : "—",
    })),
  ];

  const companyOptions = [
    { value: "", label: "All Companies" },
    ...filteredCompanies.map((c) => ({ value: c.id.toString(), label: c.name })),
  ];

  const departmentDropdownOptions = [
    { value: "", label: "All Departments" },
    ...departmentOptions.map((d) => ({ value: d, label: d })),
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: embedded ? "auto" : "100vh" }}>
      <div style={{ display: "flex", flex: 1 }}>
        {!embedded && <Sidebar />}

        <div className="rep_emr-layout" style={{ ...emrCssVars, flex: 1 }}>
          {!embedded && (
            <AssessmentTopNav title={showAssessment ? "ASSESSMENT REPORT" : "REPORTS"} />
          )}
          <div className="rep_emr-body">
            <main className="rep_emr-main">
              {showAssessment ? (
                <>
                  {assessmentLoading ? (
                    <div className="rep_emr-assessment-status-card">
                      Loading assessment report…
                    </div>
                  ) : assessmentError ? (
                    <div className="rep_emr-assessment-status-card rep_emr-assessment-status-error">
                      {assessmentError}
                    </div>
                  ) : assessmentSubView === "list" ? (
                    <>
                      <div className="rep_emr-assessment-profile-card">
                        <div className="rep_emr-assessment-avatar">
                          {getInitials(assessmentEmployee?.name)}
                        </div>
                        <div className="rep_emr-assessment-profile-info">
                          <h2 className="rep_emr-assessment-profile-name">
                            {assessmentEmployee?.name || "—"}
                          </h2>
                          <p className="rep_emr-assessment-profile-email">
                            {assessmentEmployee?.email}
                          </p>
                          <p className="rep_emr-assessment-profile-company">
                            {assessmentEmployee?.company_name || "—"}
                          </p>
                        </div>
                      </div>

                      <div className="rep_emr-table-card">
                        <div className="rep_emr-table-scroll rep_emr-assessment-table-scroll">
                          <table className="rep_emr-table">
                            <thead>
                              <tr>
                                <th>S.No</th>
                                <th>Quiz Name</th>
                                <th>Success</th>
                                <th>Pass Score</th>
                                <th>Total Score</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {assessmentQuizzesWithOffsets.length === 0 ? (
                                <tr>
                                  <td colSpan={6} className="rep_emr-empty-cell">
                                    No quizzes found for this employee.
                                  </td>
                                </tr>
                              ) : (
                                assessmentQuizzesWithOffsets.map((quiz, idx) => (
                                  <tr key={quiz.id}>
                                    <td>{String(idx + 1).padStart(2, "0")}</td>
                                    <td className="rep_emr-name-cell">{quiz.name}</td>
                                    <td>
                                      <span
                                        className={`rep_emr-status-badge ${
                                          quiz.success
                                            ? "rep_emr-success-yes"
                                            : "rep_emr-success-no"
                                        }`}
                                      >
                                        {quiz.success ? "Yes" : "No"}
                                      </span>
                                    </td>
                                    <td>{Number(quiz.pass_score ?? 0).toFixed(2)}</td>
                                    <td>{quiz.total_score ?? "—"}</td>
                                    <td>
                                      <button
                                        type="button"
                                        className="rep_emr-view-btn"
                                        onClick={() => openAssessmentDetails(quiz.id)}
                                      >
                                        <img
                                          src={ICON_ASSESSMENT_VIEW}
                                          alt=""
                                          className="rep_emr-action-icon-img"
                                        />
                                        View Details
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="rep_emr-assessment-back-btn"
                        onClick={backToEmployees}
                      >
                        <img
                          src={ICON_ASSESSMENT_BACK}
                          alt=""
                          className="rep_emr-btn-icon-img"
                        />
                        Back
                      </button>
                    </>
                  ) : (
                    <div className="rep_emr-assessment-detail-card">
                      <h2 className="rep_emr-assessment-detail-title">View Details</h2>

                      <QuizFilterDropdown
                        options={assessmentQuizzesWithOffsets.map((q) => ({
                          value: q.id,
                          label: q.name,
                        }))}
                        value={selectedQuizId}
                        onChange={setSelectedQuizId}
                      />

                      <div className="rep_emr-question-list">
                        {selectedAssessmentQuiz ? (
                          (selectedAssessmentQuiz.questions || []).map((q, idx) => {
                            const number =
                              (selectedAssessmentQuiz.questionOffset || 0) + idx + 1;
                            return (
                              <div className="rep_emr-question" key={q.id ?? idx}>
                                <p className="rep_emr-question-text">
                                  {String(number).padStart(2, "0")}. {q.text}
                                </p>
                                <div className="rep_emr-option-row">
                                  {LIKERT_OPTIONS.map((opt) => {
                                    const isSelected = q.selected_answer === opt.key;
                                    const color = isSelected
                                      ? isAnswerCorrect(q)
                                        ? CORRECT_COLOR
                                        : INCORRECT_COLOR
                                      : undefined;
                                    return (
                                      <div
                                        className={`rep_emr-option${
                                          isSelected ? " rep_emr-option-selected" : ""
                                        }`}
                                        key={opt.key}
                                        style={color ? { color } : undefined}
                                      >
                                        <span
                                          className="rep_emr-option-dot"
                                          style={color ? { backgroundColor: color } : undefined}
                                        />
                                        {opt.label}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <p className="rep_emr-empty-cell">No quiz selected.</p>
                        )}
                      </div>

                      <button
                        type="button"
                        className="rep_emr-assessment-back-btn"
                        onClick={backToAssessmentList}
                      >
                        <img
                          src={ICON_ASSESSMENT_BACK}
                          alt=""
                          className="rep_emr-btn-icon-img"
                        />
                        Back
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <>
              <header className="rep_emr-header">
                <h1 className="rep_emr-title">Employees Report</h1>
                <p className="rep_emr-subtitle">
                  View and manage employees across all companies.
                </p>
              </header>

              <section className="rep_emr-toolbar-card">
                {/* Admin: Year -> Company -> Department pickers.
                    Customer: only a Year picker, with "All Years"
                    (all years combined) as the default. */}
                {!lockedCompanyId ? (
                  <CustomDropdown
                    icon={ICON_DATE}
                    label="Year"
                    options={yearOptions}
                    value={selectedYear}
                    placeholder="All Years"
                    onChange={handleYearChange}
                  />
                ) : (
                  <CustomDropdown
                    icon={ICON_DATE}
                    label="Year"
                    options={customerYearOptions}
                    value={customerYear}
                    placeholder="All Years"
                    onChange={setCustomerYear}
                  />
                )}

                {/* Search lives inside this box — see
                    CompanySearchDropdown above. It behaves exactly like
                    the "All Companies" box on the Analytics page. */}
                {!lockedCompanyId && (
                  <CompanySearchDropdown
                    icon={ICON_CORPORATE}
                    options={companyOptions}
                    value={selectedCompany}
                    placeholder="All Companies"
                    onChange={handleCompanyChange}
                    disabled={showAll}
                  />
                )}

                {!lockedCompanyId && (
                  <CustomDropdown
                    icon={ICON_FILTER}
                    label="Department"
                    options={departmentDropdownOptions}
                    value={selectedDepartment}
                    placeholder="All Departments"
                    onChange={setSelectedDepartment}
                  />
                )}

                <button
                  type="button"
                  className="rep_emr-export-btn"
                  onClick={handleExportExcel}
                  disabled={filtered.length === 0 || exportingExcel}
                >
                  <img src={ICON_UPLOAD} alt="" className="rep_emr-btn-icon-img" />
                  {exportingExcel ? "Preparing Excel…" : "Export Excel Report"}
                </button>
              </section>

              {successAlert && (
                <div className="rep_emr-success-alert">{successAlert}</div>
              )}
              {errorAlert && <div className="rep_emr-error-alert">{errorAlert}</div>}

              <section className={`rep_emr-stats-row${isCustomerView ? " rep_emr-stats-row-customer" : ""}`} data-reveal="stats">
                {!isCustomerView && (
                  <div className="rep_emr-stat-card">
                    <div className="rep_emr-stat-icon-box rep_emr-stat-icon-orange">
                      <img src={ICON_OFFICE} alt="" className="rep_emr-stat-icon-img" />
                    </div>
                    <div className="rep_emr-stat-info">
                      <h2 className="rep_emr-stat-number"><CountUp value={companies.length} run={!!revealed.stats} delay={150} /></h2>
                      <p className="rep_emr-stat-label">Total Companies</p>
                    </div>
                  </div>
                )}
                <div className="rep_emr-stat-card">
                  <div className="rep_emr-stat-icon-box rep_emr-stat-icon-purple">
                    <img src={ICON_EMPLOYEE_GROUP} alt="" className="rep_emr-stat-icon-img" />
                  </div>
                  <div className="rep_emr-stat-info">
                    <h2 className="rep_emr-stat-number">
                      <CountUp value={totalHeadcount || filtered.length} run={!!revealed.stats} delay={240} />
                    </h2>
                    <p className="rep_emr-stat-label">Total Employees</p>
                  </div>
                </div>
                <div className="rep_emr-stat-card">
                  <div className="rep_emr-stat-icon-box rep_emr-stat-icon-green">
                    <img src={ICON_COMPLETED} alt="" className="rep_emr-stat-icon-img" />
                  </div>
                  <div className="rep_emr-stat-info">
                    <h2 className="rep_emr-stat-number"><CountUp value={completedCount} run={!!revealed.stats} delay={330} /></h2>
                    <p className="rep_emr-stat-label">Completed</p>
                  </div>
                </div>
                <div className="rep_emr-stat-card">
                  <div className="rep_emr-stat-icon-box rep_emr-stat-icon-blue">
                    <img src={ICON_IN_PROGRESS} alt="" className="rep_emr-stat-icon-img" />
                  </div>
                  <div className="rep_emr-stat-info">
                    <h2 className="rep_emr-stat-number"><CountUp value={inProgressCount} run={!!revealed.stats} delay={420} /></h2>
                    <p className="rep_emr-stat-label">In Progress</p>
                  </div>
                </div>
              </section>

              {!hasSelection ? (
                <section className="rep_emr-empty-card">
                  <img
                    src={ICON_EMPTY_STATE}
                    alt=""
                    className="rep_emr-empty-illustration"
                  />
                  <div className="rep_emr-empty-title">Employees Report</div>
                  <div className="rep_emr-empty-desc">
                    Select a company (or a year, then company) to view its
                    employee assessment report.
                  </div>
                </section>
              ) : (
                <>
                  <section className="rep_emr-overview-row">
                    <div className={`rep_emr-card rep_emr-response-rate-card${isCustomerView ? " rep_emr-card-customer" : ""}`} data-reveal="rate">
                      <div className="rep_emr-card-head">
                        <span className="rep_emr-card-head-icon">
                          <img src={ICON_HEAD_RESPONSE} alt="" />
                        </span>
                        <span className="rep_emr-card-head-title">
                          Participation &amp; Response Rate
                        </span>
                      </div>
                      <div className="rep_emr-card-body">
                        <div className="rep_emr-response-rate-body">
                          <ResponseGauge percent={responseRate} animate={!!revealed.rate} />
                          <div className="rep_emr-response-rate-info">
                            <div className="rep_emr-info-row">
                              <span className="rep_emr-info-label">Total Employees</span>
                              <span className="rep_emr-info-value">
                                <CountUp value={totalHeadcount || "N/A"} run={!!revealed.rate} delay={500} duration={1200} />
                              </span>
                            </div>
                            <div className="rep_emr-info-row">
                              <span className="rep_emr-info-label">Total Response</span>
                              <span className="rep_emr-info-value"><CountUp value={completedCount} run={!!revealed.rate} delay={620} duration={1200} /></span>
                            </div>
                            <div className="rep_emr-info-row">
                              <span className="rep_emr-info-label">
                                Minimum Required (60%)
                              </span>
                              <span className="rep_emr-info-value"><CountUp value={minimumRequired} run={!!revealed.rate} delay={740} duration={1200} /></span>
                            </div>
                            <div className="rep_emr-info-row">
                              <span className="rep_emr-info-label">Participation Status</span>
                              <span
                                className={`rep_emr-info-value ${
                                  responseRate >= 60 ? "rep_emr-info-value-positive" : ""
                                }`}
                              >
                                {totalHeadcount
                                  ? responseRate >= 60
                                    ? "Excellent"
                                    : "Needs Follow-up"
                                  : "No headcount on file"}
                              </span>
                            </div>
                          </div>
                        </div>
                        {totalHeadcount > 0 && (
                          responseRate >= 60 ? (
                            <div className="rep_emr-success-banner">
                              <img
                                src={ICON_COMPLETED}
                                alt=""
                                className="rep_emr-success-icon-img"
                              />
                              <span>
                                Excellent Participation! Your Response rate exceeds the
                                minimum requirements
                              </span>
                            </div>
                          ) : (
                            <div className="rep_emr-warning-banner">
                              <span className="rep_emr-banner-icon rep_emr-banner-icon-warning" />
                              <span>
                                Meets minimum requirement not yet reached. Response rate is
                                below the required 60% threshold.
                              </span>
                            </div>
                          )
                        )}
                      </div>
                    </div>

                    <div className={`rep_emr-card rep_emr-response-summary-card${isCustomerView ? " rep_emr-card-customer" : ""}`} data-reveal="summary">
                      <div className="rep_emr-card-head">
                        <span className="rep_emr-card-head-icon">
                          <img src={ICON_HEAD_SUMMARY} alt="" />
                        </span>
                        <span className="rep_emr-card-head-title">Response Summary ( Overall )</span>
                      </div>
                      <div className="rep_emr-card-body">
                        <div className="rep_emr-donut-section">
                          <div className={`rep_emr-donut-chart-wrapper${revealed.summary ? " rep_emr-donut-animate" : ""}`}>
                            <svg
                              viewBox="0 0 200 200"
                              className="rep_emr-donut-svg"
                            >
                              {(() => {
                                let cumulative = 0;
                                const radius = 78;
                                const cx = 100;
                                const cy = 100;
                                const baseStroke = 28;
                                const circumference = 2 * Math.PI * radius;
                                return responseSummary.levels.map((lvl, i) => {
                                  const targetFraction = chartAnimated && revealed.summary ? lvl.fraction : 0;
                                  const dash = targetFraction * circumference;
                                  const offset = cumulative * circumference;
                                  cumulative += lvl.fraction;
                                  const isHovered = hoveredLevel === lvl.key;
                                  return (
                                    <circle
                                      key={lvl.key}
                                      cx={cx}
                                      cy={cy}
                                      r={radius}
                                      fill="none"
                                      stroke={lvl.color}
                                      strokeWidth={isHovered ? baseStroke + 6 : baseStroke}
                                      strokeDasharray={`${dash} ${circumference - dash}`}
                                      strokeDashoffset={-offset}
                                      strokeLinecap="butt"
                                      transform={`rotate(-90 ${cx} ${cy})`}
                                      className="rep_emr-donut-segment"
                                      style={{
                                        opacity: hoveredLevel && !isHovered ? 0.45 : 1,
                                        filter: isHovered
                                          ? "drop-shadow(0 0 4px rgba(0,0,0,0.25))"
                                          : "none",
                                        transitionDelay: chartAnimated
                                          ? `${i * 90}ms`
                                          : "0ms",
                                      }}
                                      onMouseEnter={() => setHoveredLevel(lvl.key)}
                                      onMouseLeave={() => setHoveredLevel(null)}
                                    />
                                  );
                                });
                              })()}
                            </svg>
                            <div className="rep_emr-donut-center-label">
                              <span className="rep_emr-donut-center-number">
                                {hoveredLevel ? (
                                  responseSummary.levels.find((l) => l.key === hoveredLevel)?.count?.toLocaleString()
                                ) : (
                                  <CountUp value={responseSummary.total} run={!!revealed.summary} delay={300} duration={1500} />
                                )}
                              </span>
                              <span className="rep_emr-donut-center-text">
                                {hoveredLevel
                                  ? responseSummary.levels.find(
                                      (l) => l.key === hoveredLevel
                                    )?.label
                                  : "Total Responses"}
                              </span>
                            </div>
                          </div>
                          <div className="rep_emr-donut-legend">
                            {responseSummary.levels.map((lvl) => (
                              <div
                                className={`rep_emr-donut-legend-item ${
                                  hoveredLevel === lvl.key
                                    ? "rep_emr-legend-item-active"
                                    : ""
                                }`}
                                key={lvl.key}
                                onMouseEnter={() => setHoveredLevel(lvl.key)}
                                onMouseLeave={() => setHoveredLevel(null)}
                              >
                                <span
                                  className="rep_emr-legend-dot"
                                  style={{ backgroundColor: lvl.color }}
                                />
                                <span className="rep_emr-legend-label">{lvl.label}</span>
                                <span className="rep_emr-legend-value">
                                  <CountUp value={lvl.count} run={!!revealed.summary} delay={500 + responseSummary.levels.indexOf(lvl) * 110} duration={1400} />(
                                  <CountUp value={lvl.percent} run={!!revealed.summary} delay={500 + responseSummary.levels.indexOf(lvl) * 110} duration={1400} />%)
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="rep_emr-response-split-row">
                          <div className="rep_emr-response-split-box">
                            <span className="rep_emr-response-split-label">
                              Positive Responses
                              <small>(Strongly Agree + Agree)</small>
                            </span>
                            <span className="rep_emr-response-split-value rep_emr-response-split-positive">
                              {responseSummary.positivePercent}%
                            </span>
                          </div>
                          <div className="rep_emr-response-split-divider" />
                          <div className="rep_emr-response-split-box">
                            <span className="rep_emr-response-split-label">
                              Negative Responses
                              <small>(Disagree + Strongly Disagree)</small>
                            </span>
                            <span className="rep_emr-response-split-value rep_emr-response-split-negative">
                              {responseSummary.negativePercent}%
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className="rep_emr-table-card" data-reveal="table">
                    {isCustomerView ? (
                      <div className="rep_emr-table-toolbar">
                        <div className="rep_emr-show-entries">
                          <span>Show Entries :</span>
                          <select
                            value={entriesPerPage}
                            onChange={(e) => setEntriesPerPage(Number(e.target.value))}
                          >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                        </div>

                        <div className="rep_emr-table-search">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="7" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                          </svg>
                          <input
                            type="text"
                            placeholder="Search Email or Name..."
                            value={tableSearch}
                            onChange={(e) => setTableSearch(e.target.value)}
                          />
                        </div>

                       <div style={{ marginLeft: "auto" }}>
  <CustomDropdown
    icon={ICON_FILTER}
    label="Department"
    options={departmentDropdownOptions}
    value={selectedDepartment}
    placeholder="All Departments"
    onChange={setSelectedDepartment}
  />
</div>
                      </div>
                    ) : (
                      <div className="rep_emr-table-company-heading">
                        {showAll ? "All Companies" : selectedCompanyObj?.name || "Employees"}
                      </div>
                    )}
                    <div className="rep_emr-table-scroll">
                      <table className="rep_emr-table">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Company</th>
                            {showYearCol && <th>Assessment Year</th>}
                            <th>Employee Name</th>
                            <th>Email ID</th>
                            <th>Department</th>
                            <th>Gender</th>
                            <th>Assessment Status</th>
                            <th>Progress</th>
                            {!isCustomerView && <th>Assessment Report</th>}
                            {!isCustomerView && <th>Actions</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedEmployees.length === 0 ? (
                            <tr>
                              <td colSpan={tableColSpan} className="rep_emr-empty-cell">
                                No employees found.
                              </td>
                            </tr>
                          ) : (
                            paginatedEmployees.map((emp, idx) => {
                              const serialNo =
                                (employeePage - 1) * EMPLOYEES_PAGE_SIZE + idx + 1;
                              const { status, progress } = getAssessmentProgress(emp);
                              const isCompleted = status === "Completed";
                              return (
                                <tr key={emp.id}>
                                  <td>{String(serialNo).padStart(2, "0")}</td>
                                  <td className="rep_emr-name-cell">{emp.company_name}</td>
                                  {showYearCol && <td>{emp.company_year}</td>}
                                  <td>{emp.name}</td>
                                  <td>{emp.email}</td>
                                  <td>{emp.department}</td>
                                  <td>{emp.gender}</td>
                                  <td>
                                    <span
                                      className={`rep_emr-status-badge ${
                                        isCompleted
                                          ? "rep_emr-status-completed"
                                          : "rep_emr-status-inprogress"
                                      }`}
                                    >
                                      <span className="rep_emr-status-dot" />
                                      {isCompleted ? "Completed" : "In Progress"}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="rep_emr-progress-cell">
                                      <span className="rep_emr-progress-percent">
                                        <CountUp value={progress} run={!!revealed.table} delay={350 + Math.min(idx, 19) * 40} duration={1000} />%
                                      </span>
                                      <div className="rep_emr-progress-track">
                                        <div
                                          className={`rep_emr-progress-fill ${
                                            isCompleted
                                              ? "rep_emr-progress-fill-completed"
                                              : "rep_emr-progress-fill-inprogress"
                                          }`}
                                          style={{ width: `${progress}%` }}
                                        />
                                      </div>
                                    </div>
                                  </td>
                                  {!isCustomerView && (
                                    <td>
                                      <button
                                        type="button"
                                        className="rep_emr-report-link-btn"
                                        onClick={() => handleAssessmentReport(emp)}
                                      >
                                        <img
                                          src={ICON_ASSESSMENT}
                                          alt=""
                                          className="rep_emr-action-icon-img"
                                        />
                                        Assessment Report
                                      </button>
                                    </td>
                                  )}
                                  {!isCustomerView && (
                                    <td>
                                      <div className="rep_emr-actions-cell">
                                        <button
                                          type="button"
                                          className="rep_emr-edit-btn"
                                          onClick={() => openEditModal(emp)}
                                        >
                                          <img
                                            src={ICON_EDIT}
                                            alt=""
                                            className="rep_emr-action-icon-img"
                                          />
                                          Edit
                                        </button>
                                        <button
                                          type="button"
                                          className="rep_emr-delete-btn"
                                          onClick={() => openDeleteModal(emp.id)}
                                        >
                                          <img
                                            src={ICON_DELETE}
                                            alt=""
                                            className="rep_emr-action-icon-img"
                                          />
                                          Delete
                                        </button>
                                      </div>
                                    </td>
                                  )}
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                    <div className="rep_emr-pagination">
                      <span className="rep_emr-pagination-info">
                        Showing {employeeRangeStart}-{employeeRangeEnd} of{" "}
                        {filtered.length} Employees
                      </span>
                      <div className="rep_emr-pagination-controls">
                        <button
                          type="button"
                          className="rep_emr-pagination-btn"
                          onClick={() => goToEmployeePage(employeePage - 1)}
                          disabled={employeePage === 1}
                          aria-label="Previous page"
                        >
                          ‹
                        </button>
                        {getPaginationRange(employeePage, totalEmployeePages).map(
                          (item, idx) =>
                            item === "..." ? (
                              <span
                                className="rep_emr-pagination-ellipsis"
                                key={`ellipsis-${idx}`}
                              >
                                …
                              </span>
                            ) : (
                              <button
                                type="button"
                                key={item}
                                className={`rep_emr-pagination-btn${
                                  item === employeePage
                                    ? " rep_emr-pagination-btn-active"
                                    : ""
                                }`}
                                onClick={() => goToEmployeePage(item)}
                              >
                                {item}
                              </button>
                            )
                        )}
                        <button
                          type="button"
                          className="rep_emr-pagination-btn"
                          onClick={() => goToEmployeePage(employeePage + 1)}
                          disabled={employeePage === totalEmployeePages}
                          aria-label="Next page"
                        >
                          ›
                        </button>
                      </div>
                    </div>
                  </section>
                </>
              )}
                </>
              )}
            </main>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {modalOpen && (
        <div className="rep_emr-modal-overlay" onClick={closeModal}>
          <div className="rep_emr-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rep_emr-modal-title">Edit Employee Details</div>
            <div className="rep_emr-modal-subtitle">
              Update Existing Employee Details
            </div>

            <label className="rep_emr-modal-label" htmlFor="company_id">
              Company
            </label>
            <div className="rep_emr-select-wrap">
              <select
                className="rep_emr-modal-input rep_emr-modal-select"
                id="company_id"
                name="company_id"
                value={modalEmployee.company_id}
                onChange={handleModalChange}
              >
                <option value="">Select Company</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.year})
                  </option>
                ))}
              </select>
            </div>

            <label className="rep_emr-modal-label" htmlFor="name">
              Name
            </label>
            <input
              className="rep_emr-modal-input"
              id="name"
              name="name"
              value={modalEmployee.name}
              onChange={handleModalChange}
              placeholder="Enter employee name"
              autoFocus
            />

            <label className="rep_emr-modal-label" htmlFor="email">
              Email
            </label>
            <input
              className="rep_emr-modal-input"
              id="email"
              name="email"
              value={modalEmployee.email}
              onChange={handleModalChange}
              placeholder="Enter employee email"
            />

            <div className="rep_emr-modal-field-row">
              <div className="rep_emr-modal-field">
                <label className="rep_emr-modal-label" htmlFor="department">
                  Department
                </label>
                <div className="rep_emr-select-wrap">
                  <select
                    className="rep_emr-modal-input rep_emr-modal-select"
                    id="department"
                    name="department"
                    value={modalEmployee.department}
                    onChange={handleModalChange}
                  >
                    <option value="">Select Department</option>
                    {departmentOptions.map((dept) => (
                      <option key={dept} value={dept}>
                        {dept}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="rep_emr-modal-field">
                <label className="rep_emr-modal-label" htmlFor="gender">
                  Gender
                </label>
                <div className="rep_emr-select-wrap">
                  <select
                    className="rep_emr-modal-input rep_emr-modal-select"
                    id="gender"
                    name="gender"
                    value={modalEmployee.gender}
                    onChange={handleModalChange}
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="rep_emr-modal-actions">
              <button
                type="button"
                className="rep_emr-modal-cancel"
                onClick={closeModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rep_emr-modal-save"
                onClick={handleModalSave}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModalOpen && (
        <div className="rep_emr-modal-overlay" onClick={closeDeleteModal}>
          <div
            className="rep_emr-modal rep_emr-delete-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rep_emr-delete-warning-icon">
              <img
                src={ICON_DELETE_ALERT}
                alt=""
                className="rep_emr-delete-warning-icon-img"
              />
            </div>
            <div className="rep_emr-modal-title rep_emr-delete-modal-title">
              Delete Information
            </div>
            <div className="rep_emr-modal-subtitle rep_emr-delete-modal-subtitle">
              This Record will be deleted
            </div>
            <div className="rep_emr-modal-actions rep_emr-modal-actions-centered">
              <button
                type="button"
                className="rep_emr-modal-keep"
                onClick={closeDeleteModal}
              >
                No, Keep it
              </button>
              <button
                type="button"
                className="rep_emr-modal-delete"
                onClick={confirmDelete}
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeReports;